
<template>
  <div class="form-groups">
    <div v-for="group in displayGroups" :key="group.key" :ref="setSectionRef(group.key)" :id="`group-${group.key}`" class="group-card">
      <div class="group-title">{{ group.name }}</div>
      
      <!-- 子表：行内编辑（类似小红书规格表） -->
      <div v-if="group.isSubTable" class="sub-table-container">
        <div v-if="subTableLoading" class="sub-table-state sub-table-state--loading">
          <el-icon class="sub-table-state__icon is-loading"><Loading /></el-icon>
          <span>加载中...</span>
        </div>
        <template v-else-if="!getSubTableData(group).length">
          <el-empty description="暂无数据" :image-size="64" />
          <div class="sub-table-actions">
            <el-button size="small" type="primary" @click="addSubTableRow(group)">添加行</el-button>
          </div>
        </template>
        <template v-else>
        <el-table :data="getSubTableData(group)" border style="width: 100%">
          <el-table-column type="index" label="序号" width="60" />
          <el-table-column
            v-for="field in getSubTableDisplayFields(group)"
            :key="field.prop"
            :prop="field.prop"
            :label="field.label || field.prop"
            :min-width="resolveSubTableColumnWidth(field)"
          >
            <template #default="scope">
              <template v-if="field.prop === 'id'">
                <span class="sub-table-readonly">{{ scope.row[field.prop] || '-' }}</span>
              </template>
              <template v-else-if="isImageField(field)">
                <div class="upload-block image-type sub-table-upload-block">
                  <div
                    class="image-thumb-list thumb-list"
                    :class="{ 'image-thumb-list--sortable': canSortSubTableImages(scope.row, field) }"
                    :ref="setSubTableImageThumbListRef(group, scope.$index, field)"
                  >
                    <div
                      v-for="(imgUrl, imgIdx) in getSubTableImageUrls(scope.row[field.prop], field)"
                      :key="`${field.prop}-${imgIdx}-${imgUrl}`"
                      class="thumb-card"
                      :class="{ 'thumb-card--draggable': canSortSubTableImages(scope.row, field) }"
                    >
                      <div class="thumb-img" :style="{ backgroundImage: `url(${imgUrl})` }" />
                      <el-button
                        v-if="field.editable !== false"
                        class="thumb-remove"
                        size="small"
                        @click="removeSubTableImage(group, scope.$index, field, imgIdx)"
                      >移除</el-button>
                    </div>
                    <div
                      v-if="canAddMoreSubTableImages(scope.row, field)"
                      class="thumb-card thumb-card--add"
                      @click="openSubTableMaterialPicker(group, scope.$index, field)"
                    >
                      <el-icon class="upload-plus"><Plus /></el-icon>
                      <div class="el-upload__text">添加</div>
                    </div>
                  </div>
                </div>
              </template>
              <template v-else-if="isSubTableNumberField(field)">
                <el-input-number
                  :model-value="toSubTableNumber(scope.row[field.prop])"
                  :min="0"
                  :precision="field.prop === 'price' || field.prop === 'costPrice' ? 2 : 0"
                  controls-position="right"
                  size="small"
                  class="sub-table-number-input"
                  @update:model-value="(val) => updateSubTableCell(group, scope.$index, field.prop, val)"
                />
              </template>
              <template v-else-if="isSelect(field)">
                <el-select
                  :model-value="scope.row[field.prop]"
                  size="small"
                  clearable
                  filterable
                  class="sub-table-inline-input"
                  @update:model-value="(val) => updateSubTableCell(group, scope.$index, field.prop, val)"
                >
                  <el-option
                    v-for="opt in getFieldOptions(field, scope.row)"
                    :key="String(opt.value ?? opt.key ?? opt.label)"
                    :label="String(opt.label ?? opt.name ?? opt.value)"
                    :value="opt.value ?? opt.key ?? opt.label"
                  />
                </el-select>
              </template>
              <template v-else-if="isSwitchField(field)">
                <el-switch
                  :model-value="normalizeSwitchValue(scope.row[field.prop])"
                  :active-value="1"
                  :inactive-value="0"
                  size="small"
                  @update:model-value="(val) => updateSubTableCell(group, scope.$index, field.prop, val)"
                />
              </template>
              <template v-else>
                <el-input
                  :model-value="scope.row[field.prop] ?? ''"
                  size="small"
                  :placeholder="resolveFieldPlaceholder(field)"
                  class="sub-table-inline-input"
                  @update:model-value="(val) => updateSubTableCell(group, scope.$index, field.prop, val)"
                />
              </template>
            </template>
          </el-table-column>
          <el-table-column label="上架" width="110" fixed="right" align="center">
            <template #default="scope">
              <div class="sub-table-status-cell">
                <el-switch
                  :model-value="normalizeSubTableStatus(scope.row.status)"
                  :active-value="1"
                  :inactive-value="0"
                  size="small"
                  @update:model-value="(val) => updateSubTableCell(group, scope.$index, 'status', val)"
                />
                <span class="sub-table-status-label">{{ normalizeSubTableStatus(scope.row.status) === 1 ? '上架' : '下架' }}</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="80" fixed="right" align="center">
            <template #default="scope">
              <el-button type="danger" link size="small" @click="deleteSubTableRow(group, scope.$index)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>
        <div class="sub-table-actions">
          <el-button size="small" type="primary" @click="addSubTableRow(group)">添加行</el-button>
        </div>
        </template>
      </div>
      
      <!-- 普通字段渲染 -->
      <div v-else class="meta-form-renderer" :style="gridStyle">
        <div
          v-for="field in group.fields"
          v-show="isFieldVisible(field)"
          :key="field.prop"
          class="form-cell"
          :style="cellStyle(field)"
        >
          <el-form-item
            :prop="field.prop"
            :required="field.required"
            :rules="resolveRules(field)"
            class="form-item"
          >
            <template #label>
              <span class="field-label">{{ resolveFieldLabel(field) }}</span>
            </template>
            <template v-if="isSwitchField(field)">
              <div class="config-switch-cell">
                <el-switch
                  :model-value="normalizeSwitchValue(modelValue[field.prop])"
                  :active-value="1"
                  :inactive-value="0"
                  :disabled="field.editable === false"
                  @update:model-value="(val) => updateFieldValue(field.prop, val)"
                />
                <span class="config-switch-label">{{ normalizeSwitchValue(modelValue[field.prop]) === 1 ? '开启' : '关闭' }}</span>
              </div>
            </template>
            <template v-else-if="isImageField(field)">
              <div class="upload-block image-type">
                <div
                  class="image-thumb-list thumb-list"
                  :class="{ 'image-thumb-list--sortable': canSortImages(field) }"
                  :ref="setImageThumbListRef(field)"
                >
                  <div
                    v-for="(url, idx) in imageValueList(field)"
                    :key="`${field.prop}-${url}-${idx}`"
                    class="thumb-card"
                    :class="{ 'thumb-card--draggable': canSortImages(field) }"
                  >
                    <div class="thumb-img" :style="{ backgroundImage: `url(${normalizeUrl(url)})` }" />
                    <el-button
                      v-if="field.editable !== false"
                      class="thumb-remove"
                      size="small"
                      @click="removeImage(field, idx)"
                    >移除</el-button>
                  </div>
                  <div
                    v-if="canAddMoreImages(field)"
                    class="thumb-card thumb-card--add"
                    @click="openMaterialPicker(field)"
                  >
                    <el-icon class="upload-plus"><Plus /></el-icon>
                    <div class="el-upload__text">添加</div>
                  </div>
                </div>
              </div>
            </template>
            <template v-else-if="isUpload(field)">
              <div class="upload-block">
                <el-upload
                  class="upload-field"
                  :action="uploadAction"
                  name="file"
                  :show-file-list="true"
                  :auto-upload="true"
                  :multiple="isUploadMultiple(field)"
                  :accept="resolveAccept(field)"
                  :before-upload="beforeUpload"
                  :file-list="ensureFileList(field.prop)"
                  :on-change="(_, fileList) => onUploadChange(field, fileList)"
                  :on-remove="(_, fileList) => onUploadChange(field, fileList)"
                  :on-success="(resp, file, fileList) => onUploadSuccess(field, resp, fileList)"
                  :ref="setUploadRef(field.prop)"
                >
                  <el-button type="primary">点击上传</el-button>
                  <template #tip>
                    <div class="el-upload__tip">支持上传文件</div>
                  </template>
                </el-upload>
              </div>
            </template>
            <template v-else-if="isRichText(field)">
              <RichTextEditor
                :key="`rich-${field.prop}-${modelValue?.id ?? 'new'}`"
                :editor-key="modelValue?.id ?? 'new'"
                :model-value="modelValue[field.prop]"
                :label="field.label || field.prop"
                :placeholder="resolveFieldPlaceholder(field)"
                :disabled="field.editable === false"
                @update:model-value="(val) => updateFieldValue(field.prop, val)"
              />
            </template>
            <template v-else-if="isDateTimeField(field)">
              <el-date-picker
                :model-value="normalizeDateTimeValue(modelValue[field.prop])"
                type="datetime"
                format="YYYY-MM-DD HH:mm:ss"
                value-format="YYYY-MM-DD HH:mm:ss"
                :placeholder="resolveFieldPlaceholder(field)"
                :disabled="field.editable === false"
                clearable
                style="width: 100%"
                @update:model-value="(val) => updateFieldValue(field.prop, val)"
              />
            </template>
            <template v-else-if="isDateField(field)">
              <el-date-picker
                :model-value="normalizeDateValue(modelValue[field.prop])"
                type="date"
                format="YYYY-MM-DD"
                value-format="YYYY-MM-DD"
                :placeholder="resolveFieldPlaceholder(field)"
                :disabled="field.editable === false"
                clearable
                style="width: 100%"
                @update:model-value="(val) => updateFieldValue(field.prop, val)"
              />
            </template>
            <template v-else-if="isCategoryTreeField(field)">
              <el-tree-select
                class="category-tree-select"
                :model-value="parseCategoryTreeValue(modelValue[field.prop])"
                :data="getCategoryTreeData(field)"
                :props="categoryTreeProps"
                :loading="!!loadingFields[field.prop]"
                :placeholder="resolveFieldPlaceholder(field)"
                :disabled="field.editable === false"
                multiple
                show-checkbox
                check-strictly
                check-on-click-node
                filterable
                clearable
                collapse-tags
                collapse-tags-tooltip
                node-key="value"
                style="width: 100%"
                @update:model-value="(val) => updateCategoryTreeValue(field, val)"
              />
            </template>
            <template v-else-if="isSelect(field)">
              <el-select
                :model-value="field.multiple ? parseMultiSelectValue(modelValue[field.prop]) : modelValue[field.prop]"
                :multiple="!!field.multiple"
                :placeholder="resolveFieldPlaceholder(field)"
                :disabled="field.editable === false"
                clearable
                filterable
                collapse-tags
                collapse-tags-tooltip
                style="width: 100%"
                @update:model-value="(val) => updateFieldValue(field.prop, field.multiple ? formatMultiSelectValue(val) : val)"
              >
                <el-option
                  v-for="opt in applyOptionValuePrefix(field, getFieldOptions(field))"
                  :key="String(opt.value !== undefined ? opt.value : (opt.key !== undefined ? opt.key : opt.label))"
                  :label="String(opt.label ?? opt.name ?? opt.title ?? opt.value)"
                  :value="opt.value ?? opt.key ?? opt.label"
                />
              </el-select>
            </template>
            <component
              v-else
              :is="resolveFieldComponent(field)"
              v-model="modelValue[field.prop]"
              v-bind="resolveAttrs(field, getFieldOptions(field))"
              :key="`${field.prop}-${getFieldOptions(field).length}`"
            />
          </el-form-item>
        </div>
      </div>
    </div>
  </div>
  
  <!-- 子表编辑弹窗 -->
  <el-dialog
    v-model="subTableDialogVisible"
    :title="editingSubTableIndex === -1 ? '添加行' : '编辑行'"
    width="600px"
  >
    <el-form :model="editingSubTableRow" label-width="120px">
      <el-form-item
        v-for="field in editingSubTableGroup?.fields || []"
        :key="field.prop"
        :label="field.label || field.prop"
        :required="field.required"
      >
        <!-- 图片上传 -->
        <template v-if="isImage(field)">
          <div class="upload-block image-type">
            <el-upload
              class="upload-field"
              drag
              list-type="picture-card"
              :show-file-list="true"
              :auto-upload="true"
              :action="uploadAction"
              name="file"
              :accept="resolveAccept(field)"
              :before-upload="beforeUpload"
              :on-success="(resp) => onSubTableUploadSuccess(field, resp)"
            >
              <el-icon class="upload-plus"><Plus /></el-icon>
              <div class="el-upload__text">添加</div>
            </el-upload>
          </div>
        </template>
        <!-- 文件上传 -->
        <template v-else-if="isUpload(field)">
          <div class="upload-block">
            <el-upload
              class="upload-field"
              :action="uploadAction"
              name="file"
              :show-file-list="true"
              :auto-upload="true"
              :accept="resolveAccept(field)"
              :before-upload="beforeUpload"
              :on-success="(resp) => onSubTableUploadSuccess(field, resp)"
            >
              <el-button type="primary">点击上传</el-button>
              <template #tip>
                <div class="el-upload__tip">支持上传文件</div>
              </template>
            </el-upload>
          </div>
        </template>
        <!-- 其他字段 -->
        <component
          v-else
          :is="resolveFieldComponent(field)"
          v-model="editingSubTableRow[field.prop]"
          v-bind="resolveAttrs(field, getFieldOptions(field))"
          style="width: 100%"
        >
          <template v-if="isSelect(field)">
            <el-option
              v-for="opt in getFieldOptions(field)"
              :key="opt.value !== undefined ? opt.value : (opt.key !== undefined ? opt.key : opt.label)"
              :label="opt.label ?? opt.name ?? opt.value"
              :value="opt.value ?? opt.key ?? opt.label"
            />
          </template>
        </component>
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="subTableDialogVisible = false">取消</el-button>
      <el-button type="primary" @click="saveSubTableRow">确定</el-button>
    </template>
  </el-dialog>

  <MaterialPickerDialog
    v-model="pickerVisible"
    :multiple="resolvePickerMultiple()"
    :limit="resolvePickerLimit()"
    :selected-count-base="resolvePickerSelectedCountBase()"
    @confirm="onMaterialPickerConfirm"
  />
</template>

<script setup>
import { computed, reactive, nextTick, ref, watch, onBeforeUnmount } from 'vue'
import { ElMessage } from 'element-plus'
import Sortable from 'sortablejs'
import { Plus, Loading } from '@element-plus/icons-vue'
import axios from 'axios'
import { getProvinces, getCities, getDistricts } from '@/utils/areaDataLoader'
import RichTextEditor from '@/components/RichTextEditor.vue'
import MaterialPickerDialog from '@/components/MaterialPickerDialog.vue'
import { normalizeImgUrl, extractUploadPaths } from '@/utils/mediaUrl'

const props = defineProps({
  fields: { type: Array, default: () => [] },
  modelValue: { type: Object, required: true },
  subTableLoading: { type: Boolean, default: false },
})
const emit = defineEmits(['update:modelValue'])

// 动态选项缓存
const dynamicOptions = reactive({})

const uploadAction = '/api/upload/image'

const gridStyle = {
  display: 'grid',
  gridTemplateColumns: '1fr',
  gridAutoFlow: 'row dense',
  gap: '8px 16px',
  width: '100%',
}

const mapType = (t) => {
  const tp = (t || '').toLowerCase()
  if (['richtext', 'rich-text', 'rich_text', 'editor', '富文本'].includes(tp)) return 'richtext'
  if (['text', 'textarea', 'remark', '长文本'].includes(tp)) return 'textarea'
  if (['select', 'dict', '下拉'].includes(tp)) return 'select'
  if (['object', 'ref', 'reference', '对象', '参照'].includes(tp)) return 'select'
  if (['tree', 'tree-select', 'treeselect', 'category-tree', 'category_tree', 'cascader'].includes(tp)) return 'category-tree'
  if (['int', 'long', 'number', 'numeric', '数字', '整数'].includes(tp)) return 'number'
  if (['switch', 'bool', 'boolean', '开关'].includes(tp)) return 'switch'
  if (['image', 'img', '图片'].includes(tp)) return 'image'
  if (['upload', '上传', 'file', '附件', '文件'].includes(tp)) return 'upload'
  if (['date', '日期'].includes(tp)) return 'date'
  if (['datetime', 'timestamp', '日期时间', '时间日期'].includes(tp)) return 'datetime'
  return 'input'
}

const resolveType = (field) => {
  // 动态字段类型白名单：只对特定字典应用动态类型逻辑
  const DYNAMIC_TYPE_DICT_WHITELIST = ['web_config_type']
  const comp = String(field?.component || '').toLowerCase()
  if (comp === 'category-tree' || comp === 'el-tree-select' || comp.includes('tree-select')) return 'category-tree'
  
  // 动态字段类型：根据依赖字段的值决定类型
  if (field?.dynamicType && field?.dependsOn) {
    // 检查依赖字段是否在白名单中
    const dependField = props.fields?.find(f => f.prop === field.dependsOn || f.field === field.dependsOn)
    const dependDictCode = dependField?.dictCode || dependField?.dictcode
    
    // 只对白名单中的字典应用动态类型逻辑
    if (dependDictCode && DYNAMIC_TYPE_DICT_WHITELIST.includes(dependDictCode)) {
      const dependValue = props.modelValue?.[field.dependsOn]
      // 1 = 文本(textarea), 2 = 图片, 3 = 文件, 4 = 开关
      if (dependValue === 1 || dependValue === '1') return 'textarea'
      if (dependValue === 2 || dependValue === '2') return 'image'
      if (dependValue === 3 || dependValue === '3') return 'upload'
      if (dependValue === 4 || dependValue === '4') return 'switch'
    }
  }
  
  const base = mapType(field?.type)
  if (base === 'image') return 'image'
  if (base === 'upload') return 'upload'
  if (base !== 'input') return base
  if ((field?.options || []).length) return 'select'
  if (field?.dictCode || field?.dictcode) return 'select'
  if (comp.includes('upload')) return 'upload'
  if (comp.includes('image')) return 'image'
  return base
}

const isFieldVisible = (field) => {
  if (!props.fields || !field?.prop) return true
  const fieldKeys = [field.prop, field.field].filter(Boolean)
  for (const triggerField of props.fields) {
    const rules = triggerField.displayRules
    if (!rules || resolveType(triggerField) !== 'select') continue
    const isControlled = Object.values(rules).some((showFields) =>
      Array.isArray(showFields) && fieldKeys.some((key) => showFields.includes(key))
    )
    if (!isControlled) continue
    const triggerValue = String(props.modelValue[triggerField.prop] ?? '')
    const showFields = rules[triggerValue]
    if (showFields !== undefined && Array.isArray(showFields)) {
      if (!fieldKeys.some((key) => showFields.includes(key))) {
        return false
      }
    } else {
      return false
    }
  }
  return true
}

const isRichText = (field) => resolveType(field) === 'richtext'
const isSwitchField = (field) => resolveType(field) === 'switch'
const isDateTimeField = (field) => resolveType(field) === 'datetime'
const isDateField = (field) => resolveType(field) === 'date'
const isCategoryTreeField = (field) => resolveType(field) === 'category-tree'

const normalizeSwitchValue = (val) => {
  if (val === 1 || val === '1' || val === true) return 1
  return 0
}

const parseMultiSelectValue = (val) => {
  if (val === null || val === undefined || val === '') return []
  if (Array.isArray(val)) return val
  return String(val).split(',').map((v) => v.trim()).filter(Boolean)
}

const formatMultiSelectValue = (val) => {
  if (!Array.isArray(val)) return val
  return val.filter((v) => v !== '' && v !== null && v !== undefined).join(',')
}

const applyOptionValuePrefix = (field, options) => {
  const prefix = field?.optionValuePrefix || field?.option_value_prefix || field?.optionPrefix
  if (!prefix || !Array.isArray(options)) return options
  return options.map((opt) => ({
    ...opt,
    value: prefix + (opt.value ?? opt.id ?? opt.key ?? ''),
  }))
}

const resolveFieldLabel = (field) => {
  if (field?.prop === 'value' && isSwitchField(field)) {
    return '是否启用'
  }
  return field?.label || field?.prop || ''
}

const normalizeDateTimeValue = (val) => {
  if (val === null || val === undefined || val === '') return ''
  const s = String(val).trim()
  if (!s) return ''
  if (/^\d{4}-\d{2}-\d{2}T/.test(s)) {
    return s.replace('T', ' ').replace(/\.\d+Z?$/, '').replace(/Z$/, '')
  }
  return s
}

const normalizeDateValue = (val) => {
  const normalized = normalizeDateTimeValue(val)
  return normalized ? normalized.slice(0, 10) : ''
}

const updateFieldValue = (prop, val) => {
  emit('update:modelValue', { ...(props.modelValue || {}), [prop]: val })
}

const categoryTreeProps = {
  label: 'label',
  children: 'children',
  disabled: 'disabled',
}

const extractOptionRecords = (raw) => {
  if (Array.isArray(raw)) return raw
  if (!raw || typeof raw !== 'object') return []
  if (Array.isArray(raw.records)) return raw.records
  if (Array.isArray(raw.list)) return raw.list
  if (Array.isArray(raw.rows)) return raw.rows
  if (Array.isArray(raw.data)) return raw.data
  return []
}

const normalizeSelectOptions = (records) => {
  if (!Array.isArray(records)) return []
  return records.map((item) => {
    if (!item || typeof item !== 'object') return { value: String(item), label: String(item) }
    const rawValue = item.id ?? item.value ?? item.key ?? item.code
    return {
      ...item,
      value: rawValue,
      label: String(item.label ?? item.name ?? item.title ?? item.nickName ?? item.realName ?? item.categoryName ?? rawValue),
    }
  })
}

const normalizeCategoryTreeNode = (item) => {
  if (!item || typeof item !== 'object') return null
  const rawId = item.id ?? item.value ?? item.categoryId ?? item.category_id
  if (rawId === undefined || rawId === null || rawId === '') return null
  const value = String(rawId)
  const rawChildren = Array.isArray(item.children) ? item.children : []
  return {
    ...item,
    id: rawId,
    value,
    label: String(item.name ?? item.label ?? item.title ?? item.categoryName ?? item.category_name ?? rawId),
    pid: item.pid ?? item.parentId ?? item.parent_id ?? item.parent ?? 0,
    children: rawChildren.map(normalizeCategoryTreeNode).filter(Boolean),
  }
}

const isRootCategoryPid = (pid) => {
  if (pid === undefined || pid === null || pid === '') return true
  const str = String(pid)
  return str === '0' || str === '-1'
}

const normalizeCategoryTree = (raw) => {
  const records = extractOptionRecords(raw)
  const nodes = records.map(normalizeCategoryTreeNode).filter(Boolean)
  if (nodes.some((node) => Array.isArray(node.children) && node.children.length > 0)) {
    return nodes
  }
  const map = new Map()
  nodes.forEach((node) => {
    map.set(String(node.value), { ...node, children: [] })
  })
  const tree = []
  nodes.forEach((node) => {
    const next = map.get(String(node.value))
    const parentKey = String(node.pid ?? '')
    if (!isRootCategoryPid(node.pid) && parentKey !== String(node.value) && map.has(parentKey)) {
      map.get(parentKey).children.push(next)
    } else {
      tree.push(next)
    }
  })
  return tree
}

const parseCategoryTreeValue = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item ?? '').trim()).filter(Boolean)
  }
  if (value === undefined || value === null || value === '') return []
  return String(value).split(',').map((item) => item.trim()).filter(Boolean)
}

const getCategoryTreeData = (field) => {
  const options = dynamicOptions[field.prop]
  return Array.isArray(options) ? options : []
}

const categoryTreeAutoAncestors = {}

const getCategoryTreeAutoAncestorSet = (prop) => {
  if (!categoryTreeAutoAncestors[prop]) {
    categoryTreeAutoAncestors[prop] = new Set()
  }
  return categoryTreeAutoAncestors[prop]
}

const buildCategoryTreeIndex = (nodes = []) => {
  const index = new Map()
  let order = 0
  const walk = (list, parentValue = null) => {
    ;(Array.isArray(list) ? list : []).forEach((node) => {
      if (!node) return
      const value = String(node.value ?? node.id ?? '').trim()
      if (!value) return
      index.set(value, {
        node,
        parentValue,
        order: order++,
      })
      walk(node.children || [], value)
    })
  }
  walk(nodes)
  return index
}

const getCategoryTreeAncestors = (value, index) => {
  const ancestors = []
  const seen = new Set()
  let current = index.get(String(value))
  while (current?.parentValue && !seen.has(current.parentValue)) {
    const parentValue = current.parentValue
    ancestors.unshift(parentValue)
    seen.add(parentValue)
    current = index.get(parentValue)
  }
  return ancestors
}

const isCategoryTreeDescendantOf = (value, ancestorValue, index) => {
  const ancestor = String(ancestorValue)
  const seen = new Set()
  let current = index.get(String(value))
  while (current?.parentValue && !seen.has(current.parentValue)) {
    if (current.parentValue === ancestor) return true
    seen.add(current.parentValue)
    current = index.get(current.parentValue)
  }
  return false
}

const sortCategoryTreeValues = (values, index) => {
  return [...values].sort((a, b) => {
    const orderA = index.get(String(a))?.order
    const orderB = index.get(String(b))?.order
    if (orderA === undefined && orderB === undefined) return 0
    if (orderA === undefined) return 1
    if (orderB === undefined) return -1
    return orderA - orderB
  })
}

const normalizeCategoryTreeSelection = (field, value) => {
  const prop = field?.prop
  const rawValues = parseCategoryTreeValue(value)
  if (!prop || rawValues.length === 0) {
    if (prop) getCategoryTreeAutoAncestorSet(prop).clear()
    return []
  }

  const index = buildCategoryTreeIndex(getCategoryTreeData(field))
  const rawSet = new Set(rawValues)
  const selected = new Set(rawValues)
  const autoAncestors = getCategoryTreeAutoAncestorSet(prop)

  rawValues.forEach((item) => {
    getCategoryTreeAncestors(item, index).forEach((ancestor) => {
      if (!selected.has(ancestor)) {
        selected.add(ancestor)
        autoAncestors.add(ancestor)
      }
    })
  })

  ;[...autoAncestors].forEach((ancestor) => {
    if (!selected.has(ancestor)) {
      autoAncestors.delete(ancestor)
      return
    }
    const stillNeeded = rawValues.some((item) =>
      item !== ancestor
      && !autoAncestors.has(item)
      && isCategoryTreeDescendantOf(item, ancestor, index)
    )
    if (!stillNeeded && rawSet.has(ancestor)) {
      selected.delete(ancestor)
      autoAncestors.delete(ancestor)
    }
  })

  return sortCategoryTreeValues(selected, index)
}

const updateCategoryTreeValue = (field, value) => {
  const values = normalizeCategoryTreeSelection(field, value)
  updateFieldValue(field.prop, values.join(','))
}

const resolveFieldComponent = (field) => {
  const tp = resolveType(field)
  if (tp === 'textarea') return 'el-input'
  if (tp === 'select') return 'el-select'
  if (tp === 'number') return 'el-input'
  if (tp === 'switch') return 'el-switch'
  if (tp === 'upload') return 'el-upload'
  if (tp === 'date' || tp === 'datetime') return 'el-date-picker'
  return 'el-input'
}

// 动态加载选项
const loadingFields = reactive({})

const loadFieldOptions = async (field) => {
  const apiUrl = field.apiUrl
  if (!apiUrl) return
  
  console.log('loadFieldOptions 调用:', field.prop, field.apiUrl)
  
  loadingFields[field.prop] = true
  
  try {
    const startTime = Date.now()
    
    // 优先使用缓存的省市区数据加载器
    if (field.prop === 'province') {
      const options = await getProvinces()
      dynamicOptions[field.prop] = options
      const duration = Date.now() - startTime
      console.log('省份数据已加载(缓存):', options.length, '条', `耗时: ${duration}ms`)
      return
    }
    
    if (field.prop === 'city' && field.dependsOn === 'province') {
      const provinceId = props.modelValue[field.dependsOn]
      if (!provinceId) {
        dynamicOptions[field.prop] = []
        console.log('省份未选择，清空城市选项')
        return
      }
      const options = await getCities(provinceId)
      dynamicOptions[field.prop] = options
      const duration = Date.now() - startTime
      console.log('城市数据已加载(缓存):', options.length, '条', `耗时: ${duration}ms`)
      return
    }
    
    if (field.prop === 'district' && field.dependsOn === 'city') {
      const cityId = props.modelValue[field.dependsOn]
      if (!cityId) {
        dynamicOptions[field.prop] = []
        console.log('城市未选择，清空区县选项')
        return
      }
      const options = await getDistricts(cityId)
      dynamicOptions[field.prop] = options
      const duration = Date.now() - startTime
      console.log('区县数据已加载(缓存):', options.length, '条', `耗时: ${duration}ms`)
      return
    }
    
    // 其他字段使用通用的 API 加载逻辑
    const params = {}
    if (isCategoryTreeField(field) || isSelect(field)) {
      params.size = field.pageSize || field.size || 999
    }
    
    // 如果有 dependsOn，检查依赖字段的值
    if (field.dependsOn) {
      const dependValue = props.modelValue[field.dependsOn]
      console.log('依赖字段值:', field.dependsOn, '=', dependValue)
      if (!dependValue) {
        dynamicOptions[field.prop] = []
        console.log('依赖字段无值，清空选项')
        return
      }
      
      // 根据 dependsOn 字段构建参数
      if (field.dependsOn === 'province') {
        params.provinceId = dependValue
      } else if (field.dependsOn === 'city') {
        params.cityId = dependValue
      } else {
        // 通用依赖参数：dependsOn 字段名 + Id
        params[field.dependsOn + 'Id'] = dependValue
      }
    }
    
    console.log('发起 API 请求:', apiUrl, params)
    const response = await axios.get(apiUrl, { params })
    
    const duration = Date.now() - startTime
    console.log('API 响应:', response.data, `耗时: ${duration}ms`)
    
    const responseCode = response.data?.code
    const responseOk = responseCode === undefined || responseCode === 200 || responseCode === 0 || response.data?.success === true
    if (response.data && responseOk) {
      const optionData = response.data?.data ?? response.data
      dynamicOptions[field.prop] = isCategoryTreeField(field)
        ? normalizeCategoryTree(optionData)
        : (isSelect(field) ? normalizeSelectOptions(extractOptionRecords(optionData)) : extractOptionRecords(optionData))
      if (isCategoryTreeField(field)) {
        const currentValues = parseCategoryTreeValue(props.modelValue?.[field.prop])
        const normalizedValues = normalizeCategoryTreeSelection(field, currentValues)
        if (normalizedValues.join(',') !== currentValues.join(',')) {
          updateFieldValue(field.prop, normalizedValues.join(','))
        }
      }
      console.log('选项已加载:', field.prop, dynamicOptions[field.prop].length, '条')
    }
  } catch (error) {
    console.error(`加载 ${field.label} 选项失败:`, error)
    dynamicOptions[field.prop] = []
  } finally {
    loadingFields[field.prop] = false
  }
}

// 添加一个计算属性来获取字典选项
// rowData 参数用于子表场景，从行数据中获取已选中项的名称
const getFieldOptions = (field, rowData = null) => {
  // 优先使用动态加载的选项
  if (dynamicOptions[field.prop] && Array.isArray(dynamicOptions[field.prop])) {
    let options = [...dynamicOptions[field.prop]]
    
    // 对于省市区字段，如果当前有值但选项列表中找不到，添加一个临时选项
    if (['province', 'city', 'district'].includes(field.prop)) {
      const currentValue = props.modelValue[field.prop]
      const nameField = field.prop + 'Name' // provinceName, cityName, districtName
      const currentName = props.modelValue[nameField]
      
      console.log(`${field.prop} 检查临时选项:`, {
        currentValue,
        nameField,
        currentName,
        modelValue: props.modelValue
      })
      
      if (currentValue && currentName) {
        // 检查选项列表中是否已经有这个值（转换为字符串比较）
        const currentValueStr = String(currentValue)
        const exists = options.some(opt => String(opt.value) === currentValueStr)
        
        if (!exists) {
          // 添加临时选项（确保 value 类型与 modelValue 一致）
          options.unshift({
            value: currentValue, // 保持原始类型
            label: currentName,
            id: currentValue
          })
          console.log(`${field.prop} 添加临时选项:`, currentName, currentValue)
        } else {
          console.log(`${field.prop} 选项已存在:`, options.find(opt => String(opt.value) === currentValueStr))
        }
      } else {
        console.log(`${field.prop} 缺少值或名称，无法添加临时选项`)
      }
    }
    
    // 对于子表中的 ref 字段，如果配置了 voProp，从行数据中获取已选中项的名称
    const voProp = field.voProp || field.vo_prop || field.voField
    if (rowData && voProp) {
      const currentValue = rowData[field.prop]
      const currentName = rowData[voProp]
      if (currentValue && currentName) {
        const currentValueStr = String(currentValue)
        const exists = options.some(opt => String(opt.value) === currentValueStr || String(opt.id) === currentValueStr)
        if (!exists) {
          options.unshift({
            value: currentValue,
            label: currentName,
            id: currentValue,
            name: currentName
          })
        }
      }
    }
    
    return options
  }
  
  // 如果字段已经有 options，直接使用
  if (field.options && Array.isArray(field.options) && field.options.length) {
    let options = [...field.options]
    // 对于子表中的 ref 字段，如果配置了 voProp，从行数据中获取已选中项的名称
    const voProp2 = field.voProp || field.vo_prop || field.voField
    if (rowData && voProp2) {
      const currentValue = rowData[field.prop]
      const currentName = rowData[voProp2]
      if (currentValue && currentName) {
        const currentValueStr = String(currentValue)
        const exists = options.some(opt => String(opt.value) === currentValueStr || String(opt.id) === currentValueStr)
        if (!exists) {
          options.unshift({
            value: currentValue,
            label: currentName,
            id: currentValue,
            name: currentName
          })
        }
      }
    }
    return options
  }
  
  // 如果没有 options 但有 dictCode，尝试从缓存或配置中获取
  const dictCode = field.dictCode || field.dictcode
  if (dictCode) {
    // 这里可以从全局字典缓存中获取，或者返回空数组让父组件负责加载
    return []
  }
  
  // 对于子表中的 ref 字段，即使没有 options，也要从行数据中获取已选中项
  const voProp3 = field.voProp || field.vo_prop || field.voField
  if (rowData && voProp3) {
    const currentValue = rowData[field.prop]
    const currentName = rowData[voProp3]
    if (currentValue && currentName) {
      return [{
        value: currentValue,
        label: currentName,
        id: currentValue,
        name: currentName
      }]
    }
  }
  
  return field.options || []
}

const resolveFieldPlaceholder = (field) => {
  const custom = String(field?.placeholder ?? '').trim()
  if (custom) return custom
  return field?.label || field?.prop || ''
}

const resolveAttrs = (field, options = null) => {
  const tp = resolveType(field)
  const placeholder = resolveFieldPlaceholder(field)
  if (tp === 'textarea') return { type: 'textarea', rows: 3, placeholder }
  if (tp === 'number') return { type: 'number', placeholder }
  if (tp === 'select') {
    const attrs = { placeholder, clearable: true, filterable: true }
    // 如果有 options，添加到属性中
    const opts = options || field.options
    if (opts && Array.isArray(opts) && opts.length) {
      attrs.options = opts
    }
    return attrs
  }
  if (tp === 'switch') return { activeValue: 1, inactiveValue: 0 }
  if (tp === 'date') {
    return {
      type: 'date',
      format: 'YYYY-MM-DD',
      'value-format': 'YYYY-MM-DD',
      placeholder,
      clearable: true,
      style: 'width: 100%',
    }
  }
  if (tp === 'datetime') {
    return {
      type: 'datetime',
      format: 'YYYY-MM-DD HH:mm:ss',
      'value-format': 'YYYY-MM-DD HH:mm:ss',
      placeholder,
      clearable: true,
      style: 'width: 100%',
    }
  }
  return { placeholder }
}

const resolveRules = (field) => {
  const rules = []
  if (field.required) {
    if (isSwitchField(field)) {
      rules.push({
        validator: (_rule, value, callback) => {
          if (value === 0 || value === 1 || value === '0' || value === '1') {
            callback()
            return
          }
          callback(new Error(`${resolveFieldLabel(field)}未填写`))
        },
        trigger: 'change',
      })
    } else {
      rules.push({
        required: true,
        message: `${field.label || field.prop}未填写`,
        trigger: ['blur', 'change']
      })
    }
  }
  return rules
}

// 子表相关函数
const resolveSubTableParentField = (group) => {
  if (!group?.isSubTable) return group?.parentField
  return group.fields?.find((f) => f.parentField)?.parentField
    || group.parentField
    || (group.name === '规格' ? 'specs' : undefined)
    || 'specs'
}

const getSubTableData = (group) => {
  const tryKeys = [
    resolveSubTableParentField(group),
    group?.parentField,
    group?.name === '规格' ? 'specs' : null,
    'specs',
    'ProductSpece',
    'produec_specs',
    'product_specs',
  ].filter(Boolean)
  for (const key of [...new Set(tryKeys)]) {
    const data = props.modelValue?.[key]
    if (Array.isArray(data)) return data
  }
  return []
}

const getSubTableDisplayFields = (group) => {
  return (group.fields || []).filter((f) => !f.hidden && f.prop !== 'status')
}

const resolveSubTableColumnWidth = (field) => {
  if (isSubTableImageField(field)) {
    return isSubTableMultiImageField(field) ? '420px' : '160px'
  }
  if (isSubTableNumberField(field)) return '130px'
  return field.width || '140px'
}

const isSubTableNumberField = (field) => {
  const tp = resolveType(field)
  const prop = String(field?.prop || '')
  return tp === 'number' || ['price', 'stock', 'saleLimit', 'costPrice', 'weight', 'sort', 'soldCount'].includes(prop)
}

const toSubTableNumber = (val) => {
  if (val === null || val === undefined || val === '') return null
  const n = Number(val)
  return Number.isNaN(n) ? null : n
}

const normalizeSubTableStatus = (val) => {
  if (val === null || val === undefined || val === '') return 1
  return Number(val) === 0 ? 0 : 1
}

const setSubTableRows = (group, rows) => {
  const parentField = resolveSubTableParentField(group)
  if (!parentField) return
  const updated = { ...props.modelValue }
  updated[parentField] = rows
  if (parentField !== 'specs') updated.specs = rows
  emit('update:modelValue', updated)
}

const createEmptySubTableRow = (group) => {
  const row = { status: 1 }
  getSubTableDisplayFields(group).forEach((field) => {
    if (field.prop === 'id') return
    if (isSubTableNumberField(field)) {
      row[field.prop] = field.prop === 'stock' ? 0 : null
      return
    }
    row[field.prop] = ''
  })
  return row
}

const updateSubTableCell = (group, rowIndex, prop, value) => {
  const currentData = [...getSubTableData(group)]
  if (!currentData[rowIndex]) return
  currentData[rowIndex] = { ...currentData[rowIndex], [prop]: value }
  setSubTableRows(group, currentData)
}

const onSubTableInlineUploadSuccess = (group, rowIndex, field, resp) => {
  const url = normalizeUrl(pickRespUrl(resp))
  if (!url || !field?.prop) return
  const prop = field.prop
  if (isSubTableMultiImageField(field)) {
    const row = getSubTableData(group)[rowIndex]
    const merged = [...parseSubTableImageValue(row?.[prop]), url]
    updateSubTableCell(group, rowIndex, prop, merged.join(','))
    return
  }
  updateSubTableCell(group, rowIndex, prop, url)
}

const subTableImageValueList = (row, field) => getSubTableImageUrls(row?.[field?.prop], field)

const subTablePickerLimit = (field) => {
  if (!field) return 1
  return isSubTableMultiImageField(field) ? 20 : 1
}

const canAddMoreSubTableImages = (row, field) => {
  if (!field || field.editable === false) return false
  return subTableImageValueList(row, field).length < subTablePickerLimit(field)
}

const buildSubTableSortableKey = (group, rowIndex, field) => {
  const groupKey = group?.key || group?.name || 'sub-table'
  return `sub:${groupKey}:${rowIndex}:${field?.prop}`
}

const serializeSubTableImageValue = (field, row, relImgs) => {
  const urls = (relImgs || []).map((u) => String(u || '').trim()).filter(Boolean)
  if (!isSubTableMultiImageField(field)) return urls[0] || ''
  const raw = row?.[field.prop]
  if (typeof raw === 'string' && raw.trim().startsWith('[')) {
    return JSON.stringify(urls)
  }
  if (Array.isArray(raw)) return urls
  return urls.join(',')
}

const canSortSubTableImages = (row, field) => {
  if (!field || field.editable === false) return false
  return isSubTableMultiImageField(field) && subTableImageValueList(row, field).length > 1
}

const reorderSubTableImages = (group, rowIndex, field, oldIndex, newIndex) => {
  if (oldIndex === newIndex || oldIndex == null || newIndex == null) return
  const prop = field?.prop
  if (!prop) return
  const row = getSubTableData(group)[rowIndex]
  const imgs = [...subTableImageValueList(row, field)]
  if (oldIndex < 0 || oldIndex >= imgs.length) return
  const moved = imgs.splice(oldIndex, 1)[0]
  imgs.splice(newIndex, 0, moved)
  const val = serializeSubTableImageValue(field, row, imgs.map((u) => toRelativeUrl(u)))
  updateSubTableCell(group, rowIndex, prop, val)
}

const removeSubTableImage = (group, rowIndex, field, imgIdx) => {
  const prop = field?.prop
  if (!prop) return
  const row = getSubTableData(group)[rowIndex]
  const imgs = [...subTableImageValueList(row, field)]
  imgs.splice(imgIdx, 1)
  const relImgs = imgs.map((u) => toRelativeUrl(u))
  const val = serializeSubTableImageValue(field, row, relImgs)
  updateSubTableCell(group, rowIndex, prop, val)
  nextTick(() => initSubTableImageSortable(group, rowIndex, field))
}

const addSubTableRow = (group) => {
  if (!resolveSubTableParentField(group)) return
  setSubTableRows(group, [...getSubTableData(group), createEmptySubTableRow(group)])
}

const editSubTableRow = (group, index) => {
  const currentData = getSubTableData(group)
  editingSubTableRow.value = { ...currentData[index] }
  editingSubTableGroup.value = group
  editingSubTableIndex.value = index
  subTableDialogVisible.value = true
}

const deleteSubTableRow = (group, index) => {
  setSubTableRows(group, getSubTableData(group).filter((_, i) => i !== index))
}

const onSubTableUploadSuccess = (field, resp) => {
  console.log('[子表上传成功] 字段:', field.prop, '响应:', resp)
  const url = normalizeUrl(pickRespUrl(resp))
  console.log('[子表上传成功] URL:', url)
  if (url && field.prop) {
    editingSubTableRow.value[field.prop] = url
  }
}

const saveSubTableRow = () => {
  const group = editingSubTableGroup.value
  const parentField = resolveSubTableParentField(group)
  if (!parentField) return
  const currentData = getSubTableData(group)
  const updated = { ...props.modelValue }
  const nextData = editingSubTableIndex.value === -1
    ? [...currentData, editingSubTableRow.value]
    : currentData.map((row, i) => (i === editingSubTableIndex.value ? editingSubTableRow.value : row))
  updated[parentField] = nextData
  if (parentField !== 'specs') updated.specs = nextData
  
  emit('update:modelValue', updated)
  subTableDialogVisible.value = false
}

const editingSubTableRow = ref({})
const editingSubTableGroup = ref(null)
const editingSubTableIndex = ref(-1)
const subTableDialogVisible = ref(false)

const isUploadMultiple = (field) => !!field?.multiple

const isSelect = (field) => resolveType(field) === 'select'
const isUpload = (field) => resolveType(field) === 'upload'
const isImage = (field) => resolveType(field) === 'image'

// 解析允许的文件扩展名
const resolveAccept = (field) => {
  // 从 modelValue 中读取 allowedExtensions 字段的值
  const allowedExtensions = props.modelValue?.allowedExtensions
  if (!allowedExtensions || allowedExtensions === '*') {
    return undefined // 允许所有文件类型
  }
  // 将逗号分隔的扩展名转换为 accept 格式
  const extensions = String(allowedExtensions).split(',').map(ext => ext.trim()).filter(Boolean)
  return extensions.map(ext => `.${ext}`).join(',')
}

// 验证文件类型
const beforeUpload = (file) => {
  const allowedExtensions = props.modelValue?.allowedExtensions
  
  // 如果没有配置 allowedExtensions 字段（如子表中），则不做限制
  if (!allowedExtensions || allowedExtensions === '*') {
    return true // 允许所有文件类型
  }
  
  const extensions = String(allowedExtensions).split(',').map(ext => ext.trim().toLowerCase()).filter(Boolean)
  const fileName = file.name.toLowerCase()
  const fileExt = fileName.substring(fileName.lastIndexOf('.') + 1)
  
  if (!extensions.includes(fileExt)) {
    const allowedFormats = extensions.map(ext => `.${ext}`).join('、')
    ElMessage.error({
      message: `文件类型不符合要求！当前只允许上传以下格式的文件：${allowedFormats}`,
      duration: 5000,
      showClose: true
    })
    return false
  }
  return true
}

const sectionRefs = reactive({})
const setSectionRef = (key) => (el) => {
  if (el) sectionRefs[key] = el
}

const fileLists = reactive({})
const uploadRefs = reactive({})
const sortableMap = reactive({})
const imageThumbListRefs = reactive({})
const imageSortableMap = reactive({})

const isMultiImageField = (field) => isSubTableMultiImageField(field) || isUploadMultiple(field)

const canSortImages = (field) => {
  if (!field || field.editable === false) return false
  return isMultiImageField(field) && imageValueList(field).length > 1
}

const serializeImageFieldValue = (field, relImgs) => {
  const urls = (relImgs || []).map((u) => String(u || '').trim()).filter(Boolean)
  if (!isMultiImageField(field)) return urls[0] || ''
  const raw = props.modelValue?.[field.prop]
  if (Array.isArray(raw)) return urls
  return urls.join(',')
}

const toFileList = (val) => {
  if (!val) return []
  if (Array.isArray(val)) return val.map((v, idx) => toFileObj(v, idx))
  return [toFileObj(val, 0)]
}

const normalizeUrl = (url) => {
  if (!url) return ''
  const str = String(url)
  const cleaned = str.includes('#') ? str.split('#')[0] : str
  return normalizeImgUrl(cleaned)
}

const SUB_TABLE_IMAGE_PROPS = new Set([
  'specImage', 'spec_image', 'specImages', 'spec_images',
  'detailImages', 'detail_images', 'mainImage', 'main_image',
  'carouselImages', 'carousel_images', 'image', 'images',
])

const isSubTableImageField = (field) => {
  if (resolveType(field) === 'image') return true
  const prop = String(field?.prop || '')
  const label = String(field?.label || '')
  if (SUB_TABLE_IMAGE_PROPS.has(prop)) return true
  if (/(^|_)(image|images|img|photo|pic)(_|$)/i.test(prop)) return true
  if (/主图|详情图|图片|图集|展示图/.test(label)) return true
  return false
}

const isImageField = (field) => isSubTableImageField(field)

const isSubTableMultiImageField = (field) => {
  if (field?.multiple) return true
  const prop = String(field?.prop || '').toLowerCase()
  const label = String(field?.label || '')
  if (/images|detail|carousel|display|specimages|图集|详情/.test(prop)) return true
  if (/详情图|图集|多图|展示图|多个/.test(label)) return true
  return false
}

const parseSubTableImageValue = (val) => {
  if (val == null || val === '') return []
  if (Array.isArray(val)) {
    return val.flatMap((item) => parseSubTableImageValue(item))
  }
  if (typeof val === 'object') {
    const url = val.url || val.src || pickRespUrl(val.response) || ''
    return url ? [normalizeUrl(url)] : []
  }
  const str = String(val).trim()
  if (!str) return []
  const paths = extractUploadPaths(str)
  if (paths.length) {
    return paths.map((item) => normalizeUrl(item)).filter(Boolean)
  }
  return [normalizeUrl(str)].filter(Boolean)
}

const getSubTableImageUrls = (val, field) => {
  const urls = parseSubTableImageValue(val)
  return isSubTableMultiImageField(field) ? urls : urls.slice(0, 1)
}

const toRelativeUrl = (url) => {
  if (!url) return ''
  const str = String(url)
  try {
    const u = new URL(str)
    return u.pathname + u.search + u.hash
  } catch {
    return str
  }
}

const pickRespUrl = (resp) => {
  if (!resp) return ''
  if (typeof resp === 'string') return resp
  if (resp.url) return resp.url
  if (resp.data) {
    if (typeof resp.data === 'string') return resp.data
    if (typeof resp.data === 'object' && resp.data.url) return resp.data.url
  }
  return ''
}

const toFileObj = (v, idx = 0) => {
  if (typeof v === 'string') return { uid: `img-${idx}`, name: `image-${idx}`, url: normalizeUrl(v), status: 'success' }
  if (v && typeof v === 'object')
    return {
      uid: v.uid || `img-${idx}`,
      name: v.name || `image-${idx}`,
      url: normalizeUrl(v.url || v.src || v),
      status: v.status || 'success',
    }
  return { uid: `img-${idx}`, name: `image-${idx}`, url: '', status: 'success' }
}

const normalizeImages = (val, field) => {
  const urls = parseSubTableImageValue(val)
  const multi = isSubTableMultiImageField(field) || !!field?.multiple
  return multi ? urls : urls.slice(0, 1)
}

const ensureFileList = (prop) => {
  if (!prop) return []
  if (!fileLists[prop]) {
    fileLists[prop] = toFileList(props.modelValue[prop])
    nextTick(() => initSortable(prop))
  }
  return fileLists[prop]
}

const uploadText = (prop) => {
  const list = ensureFileList(prop)
  return Array.isArray(list) ? list.map((f) => f.url).filter(Boolean).join(', ') : ''
}

const triggerUpload = (field) => {
  const ref = uploadRefs[field?.prop]
  const input = ref?.$el?.querySelector('input[type="file"]')
  if (input) input.click()
}

const removeFile = (field, idx) => {
  const prop = field?.prop
  if (!prop || !fileLists[prop]) return
  fileLists[prop].splice(idx, 1)
  emit('update:modelValue', { ...(props.modelValue || {}), [prop]: fileLists[prop].map((f) => toRelativeUrl(f.url)).filter(Boolean) })
}

const removeImage = (field, idx) => {
  const prop = field?.prop
  if (!prop) return
  const imgs = normalizeImages(props.modelValue[prop], field)
  imgs.splice(idx, 1)
  const relImgs = imgs.map((u) => toRelativeUrl(u))
  const val = serializeImageFieldValue(field, relImgs)
  fileLists[prop] = toFileList(val)
  emit('update:modelValue', { ...(props.modelValue || {}), [prop]: val })
  nextTick(() => initImageSortable(field))
}

const pickerVisible = ref(false)
const pickerField = ref(null)
const pickerSubTableContext = ref(null)

const imageValueList = (field) => normalizeImages(props.modelValue?.[field?.prop], field)

const pickerLimit = (field) => {
  if (!field) return 1
  if (field.prop === 'contentImages') return 10
  if (isUploadMultiple(field) || isSubTableMultiImageField(field)) return 20
  return 1
}

const resolvePickerMultiple = () => {
  if (pickerSubTableContext.value) {
    return isSubTableMultiImageField(pickerSubTableContext.value.field)
  }
  if (!pickerField.value) return false
  return isUploadMultiple(pickerField.value) || isSubTableMultiImageField(pickerField.value)
}

const resolvePickerLimit = () => {
  if (pickerSubTableContext.value) {
    return subTablePickerLimit(pickerSubTableContext.value.field)
  }
  return pickerField.value ? pickerLimit(pickerField.value) : 1
}

const resolvePickerSelectedCountBase = () => {
  if (pickerSubTableContext.value) {
    const { group, rowIndex, field } = pickerSubTableContext.value
    const row = getSubTableData(group)[rowIndex]
    return subTableImageValueList(row, field).length
  }
  return pickerField.value ? imageValueList(pickerField.value).length : 0
}

watch(pickerVisible, (open) => {
  if (!open) {
    pickerSubTableContext.value = null
    pickerField.value = null
  }
})

watch(
  () => (props.fields || [])
    .filter((f) => isImageField(f) && f.prop)
    .map((f) => `${f.prop}:${imageValueList(f).length}:${String(props.modelValue?.[f.prop] || '')}`)
    .join('|'),
  () => {
    nextTick(() => {
      (props.fields || []).filter((f) => isImageField(f)).forEach((field) => initImageSortable(field))
    })
  }
)

const canAddMoreImages = (field) => {
  if (!field || field.editable === false) return false
  return imageValueList(field).length < pickerLimit(field)
}

const openMaterialPicker = (field) => {
  if (!field || field.editable === false) return
  pickerSubTableContext.value = null
  pickerField.value = field
  pickerVisible.value = true
}

const openSubTableMaterialPicker = (group, rowIndex, field) => {
  if (!field || field.editable === false) return
  pickerField.value = null
  pickerSubTableContext.value = { group, rowIndex, field }
  pickerVisible.value = true
}

const onMaterialPickerConfirm = (paths) => {
  const subCtx = pickerSubTableContext.value
  if (subCtx) {
    const { group, rowIndex, field } = subCtx
    const prop = field.prop
    const multi = isSubTableMultiImageField(field)
    const limit = subTablePickerLimit(field)
    const relPaths = (paths || []).map((p) => toRelativeUrl(p)).filter(Boolean)
    const row = getSubTableData(group)[rowIndex]
    let val
    if (multi) {
      const existing = subTableImageValueList(row, field).map((u) => toRelativeUrl(u))
      const merged = [...existing, ...relPaths].filter((v, i, a) => a.indexOf(v) === i).slice(0, limit)
      val = serializeSubTableImageValue(field, row, merged)
    } else {
      val = relPaths[0] || ''
    }
    updateSubTableCell(group, rowIndex, prop, val)
    pickerVisible.value = false
    pickerSubTableContext.value = null
    nextTick(() => initSubTableImageSortable(group, rowIndex, field))
    return
  }

  const field = pickerField.value
  if (!field) return
  const prop = field.prop
  const multi = resolvePickerMultiple()
  const limit = pickerLimit(field)
  const relPaths = (paths || []).map((p) => toRelativeUrl(p)).filter(Boolean)
  let val
  if (multi) {
    const existing = imageValueList(field).map((u) => toRelativeUrl(u))
    const merged = [...existing, ...relPaths].filter((v, i, a) => a.indexOf(v) === i).slice(0, limit)
    val = serializeImageFieldValue(field, merged)
  } else {
    val = relPaths[0] || ''
  }
  fileLists[prop] = toFileList(val)
  emit('update:modelValue', { ...(props.modelValue || {}), [prop]: val })
  pickerVisible.value = false
  pickerField.value = null
  nextTick(() => initImageSortable(field))
}

const onUploadChange = (field, fileList) => {
  const prop = field.prop
  if (!prop) return
  let list = fileList
  if (!isUploadMultiple(field)) {
    list = fileList.slice(-1)
  }
  fileLists[prop] = list.map((f, idx) => {
    const respUrl = pickRespUrl(f.response)
    const finalUrl = normalizeUrl(respUrl || f.url)
    return { uid: f.uid || `img-${idx}`, name: f.name || `image-${idx}`, url: finalUrl, status: 'success' }
  })
  const urls = fileLists[prop].map((f) => toRelativeUrl(f.url)).filter(Boolean)
  const clean = urls.filter((u) => !String(u).startsWith('blob:'))
  const single = clean[0] || urls[0] || ''
  const val = isUploadMultiple(field) ? clean : single
  emit('update:modelValue', { ...(props.modelValue || {}), [prop]: val })
  nextTick(() => initSortable(prop))
}

const onUploadSuccess = (field, resp, fileList) => {
  // ensure el-upload file.url is set from backend response for preview
  console.log('[上传成功] 后端返回:', resp)
  const rawUrl = pickRespUrl(resp)
  const displayUrl = normalizeUrl(rawUrl)
  const saveUrl = toRelativeUrl(displayUrl)
  console.log('[上传成功] 提取的 URL:', rawUrl, '-> 保存:', saveUrl)
  if (displayUrl && fileList && fileList.length) {
    const last = fileList[fileList.length - 1]
    last.url = displayUrl
    last.response = resp
    last.status = 'success'
    last.uid = last.uid || `img-${fileList.length - 1}`
    const prop = field?.prop
    if (prop) {
      const val = isUploadMultiple(field) ? [saveUrl] : saveUrl
      fileLists[prop] = toFileList(val)
      emit('update:modelValue', { ...(props.modelValue || {}), [prop]: val })
      console.log('[上传成功] 更新后的 fileList:', fileLists[prop])
      console.log('[上传成功] 更新后的 modelValue:', { ...(props.modelValue || {}), [prop]: val })
      nextTick(() => initSortable(prop))
      return
    }
  }
  console.warn('[上传成功] 未找到有效 URL，使用默认处理')
  onUploadChange(field, fileList)
}

const initSortable = (prop) => {
  const ref = uploadRefs[prop]
  if (!ref || sortableMap[prop]) return
  const listEl = ref?.$el?.querySelector('.el-upload-list')
  if (!listEl) return
  sortableMap[prop] = Sortable.create(listEl, {
    animation: 150,
    onEnd: (evt) => {
      const list = fileLists[prop] || []
      const moved = list.splice(evt.oldIndex, 1)[0]
      list.splice(evt.newIndex, 0, moved)
      emit('update:modelValue', { ...(props.modelValue || {}), [prop]: list.map((f) => toRelativeUrl(f.url)).filter(Boolean) })
    },
  })
}

const destroyImageSortable = (prop) => {
  if (imageSortableMap[prop]) {
    imageSortableMap[prop].destroy()
    delete imageSortableMap[prop]
  }
}

const reorderImageField = (field, oldIndex, newIndex) => {
  if (oldIndex === newIndex || oldIndex == null || newIndex == null) return
  const prop = field?.prop
  if (!prop) return
  const imgs = [...imageValueList(field)]
  if (oldIndex < 0 || oldIndex >= imgs.length || newIndex < 0 || newIndex >= imgs.length) return
  const moved = imgs.splice(oldIndex, 1)[0]
  imgs.splice(newIndex, 0, moved)
  const val = serializeImageFieldValue(field, imgs.map((u) => toRelativeUrl(u)))
  fileLists[prop] = toFileList(val)
  emit('update:modelValue', { ...(props.modelValue || {}), [prop]: val })
}

const initImageSortable = (field) => {
  const prop = field?.prop
  if (!prop || !canSortImages(field)) {
    if (prop) destroyImageSortable(prop)
    return
  }
  const listEl = imageThumbListRefs[prop]
  if (!listEl) return
  destroyImageSortable(prop)
  imageSortableMap[prop] = Sortable.create(listEl, {
    animation: 180,
    draggable: '.thumb-card--draggable',
    filter: '.thumb-card--add',
    preventOnFilter: true,
    ghostClass: 'thumb-card--ghost',
    chosenClass: 'thumb-card--chosen',
    onEnd: (evt) => {
      reorderImageField(field, evt.oldIndex, evt.newIndex)
      nextTick(() => initImageSortable(field))
    },
  })
}

const initSubTableImageSortable = (group, rowIndex, field) => {
  const key = buildSubTableSortableKey(group, rowIndex, field)
  const row = getSubTableData(group)[rowIndex]
  if (!canSortSubTableImages(row, field)) {
    destroyImageSortable(key)
    return
  }
  const listEl = imageThumbListRefs[key]
  if (!listEl) return
  destroyImageSortable(key)
  imageSortableMap[key] = Sortable.create(listEl, {
    animation: 180,
    draggable: '.thumb-card--draggable',
    filter: '.thumb-card--add',
    preventOnFilter: true,
    ghostClass: 'thumb-card--ghost',
    chosenClass: 'thumb-card--chosen',
    onEnd: (evt) => {
      reorderSubTableImages(group, rowIndex, field, evt.oldIndex, evt.newIndex)
      nextTick(() => initSubTableImageSortable(group, rowIndex, field))
    },
  })
}

const setImageThumbListRef = (field) => (el) => {
  const prop = field?.prop
  if (!prop) return
  if (!el) {
    destroyImageSortable(prop)
    delete imageThumbListRefs[prop]
    return
  }
  imageThumbListRefs[prop] = el
  nextTick(() => initImageSortable(field))
}

const setSubTableImageThumbListRef = (group, rowIndex, field) => (el) => {
  const key = buildSubTableSortableKey(group, rowIndex, field)
  if (!el) {
    destroyImageSortable(key)
    delete imageThumbListRefs[key]
    return
  }
  imageThumbListRefs[key] = el
  nextTick(() => initSubTableImageSortable(group, rowIndex, field))
}

const refreshSubTableImageSortables = () => {
  displayGroups.value
    .filter((group) => group.isSubTable)
    .forEach((group) => {
      getSubTableData(group).forEach((row, rowIndex) => {
        group.fields.filter((f) => isImageField(f)).forEach((field) => {
          initSubTableImageSortable(group, rowIndex, field)
        })
      })
    })
}

onBeforeUnmount(() => {
  Object.keys(imageSortableMap).forEach((prop) => destroyImageSortable(prop))
})

const setUploadRef = (prop) => (el) => {
  if (el) {
    uploadRefs[prop] = el
    nextTick(() => initSortable(prop))
  }
}

const cellStyle = (field) => {
  // 单列布局，字段占整行
  return { gridColumn: '1 / -1' }
}

const groupedFields = computed(() => {
  const list = Array.isArray(props.fields) ? props.fields : []
  const groups = {}
  list.forEach((f) => {
    const name = f.category || '基础设置'
    const order = Number.isFinite(f.categoryOrder) ? Number(f.categoryOrder) : 0
    if (!groups[name]) {
      groups[name] = { name, order, fields: [] }
    }
    groups[name].fields.push(f)
  })
  return Object.values(groups)
    .sort((a, b) => a.order - b.order)
    .map((g) => ({
      ...g,
      key: g.name,
      fields: g.fields
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    }))
})

const displayGroups = computed(() =>
  groupedFields.value
    .map((g) => {
      const visibleFields = g.fields.filter((f) => !f.hidden)
      // 检查是否所有字段都是子表字段
      const isSubTable = visibleFields.length > 0 && visibleFields.every(f => f.isSubTable)
      const parentField = isSubTable
        ? (visibleFields.find((f) => f.parentField)?.parentField
          || (g.name === '规格' ? 'specs' : undefined)
          || visibleFields[0]?.parentField
          || 'specs')
        : undefined
      
      return { 
        ...g, 
        fields: visibleFields,
        isSubTable,
        parentField
      }
    })
    .filter((g) => g.fields.length)
)

watch(
  () => displayGroups.value
    .filter((group) => group.isSubTable)
    .map((group) => getSubTableData(group)
      .map((row, rowIndex) => group.fields
        .filter((f) => isImageField(f) && isSubTableMultiImageField(f))
        .map((field) => `${buildSubTableSortableKey(group, rowIndex, field)}:${subTableImageValueList(row, field).length}:${String(row[field.prop] || '')}`)
        .join(';'))
      .join('|'))
    .join('||'),
  () => {
    nextTick(() => refreshSubTableImageSortables())
  }
)

const scrollToSection = (key, container) => {
  const el = sectionRefs[key]
  if (!el) return
  if (container && container.scrollTo) {
    const top = el.offsetTop - 8
    container.scrollTo({ top, behavior: 'smooth' })
  } else {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
}

// 初始化动态选项字段
const initDynamicFields = () => {
  const allFields = props.fields || []
  console.log('初始化动态字段，总字段数:', allFields.length)
  allFields.forEach(field => {
    if (!field.apiUrl) return
    
    // 没有依赖的字段，直接加载
    if (!field.dependsOn) {
      console.log('加载字段选项:', field.prop, field.label, field.apiUrl)
      loadFieldOptions(field)
      return
    }
    
    // 有依赖的字段，如果依赖字段有值且当前字段也有值（编辑模式），则加载选项
    const dependValue = props.modelValue?.[field.dependsOn]
    const currentValue = props.modelValue?.[field.prop]
    if (dependValue && currentValue) {
      console.log('编辑模式 - 加载字段选项:', field.prop, field.label, '依赖值:', dependValue)
      loadFieldOptions(field)
    }
  })
}

// 网站配置：类型切换为开关时，变量值用 0/1
watch(() => props.modelValue?.type, (newType, oldType) => {
  const valueField = props.fields?.find((f) => f.prop === 'value' && f.dynamicType)
  if (!valueField) return
  const typeField = props.fields?.find((f) => f.prop === valueField.dependsOn || f.prop === 'type')
  const dictCode = typeField?.dictCode || typeField?.dictcode
  if (dictCode !== 'web_config_type') return
  if (newType === oldType) return

  const isSwitch = newType === 4 || newType === '4'
  const wasSwitch = oldType === 4 || oldType === '4'
  if (isSwitch) {
    const current = props.modelValue?.value
    if (current === null || current === undefined || current === '') {
      updateFieldValue('value', 0)
      return
    }
    const normalized = normalizeSwitchValue(current)
    if (normalized !== current) {
      updateFieldValue('value', normalized)
    }
    return
  }
  if (wasSwitch) {
    updateFieldValue('value', '')
  }
})

// 监听省份变化，自动加载城市
watch(() => props.modelValue?.province, (newProvinceId, oldProvinceId) => {
  if (newProvinceId !== oldProvinceId) {
    console.log('省份变化:', oldProvinceId, '->', newProvinceId)
    
    // 清空城市和区县
    if (props.modelValue.city) {
      emit('update:modelValue', { ...props.modelValue, city: null, district: null })
    }
    
    // 加载城市数据
    const cityField = props.fields?.find(f => f.prop === 'city')
    if (cityField && newProvinceId) {
      console.log('自动加载城市数据')
      loadFieldOptions(cityField)
    } else {
      dynamicOptions.city = []
    }
    
    // 清空区县选项
    dynamicOptions.district = []
  }
})

// 监听城市变化，自动加载区县
watch(() => props.modelValue?.city, (newCityId, oldCityId) => {
  if (newCityId !== oldCityId) {
    console.log('城市变化:', oldCityId, '->', newCityId)
    
    // 清空区县
    if (props.modelValue.district) {
      emit('update:modelValue', { ...props.modelValue, district: null })
    }
    
    // 加载区县数据
    const districtField = props.fields?.find(f => f.prop === 'district')
    if (districtField && newCityId) {
      console.log('自动加载区县数据')
      loadFieldOptions(districtField)
    } else {
      dynamicOptions.district = []
    }
  }
})

// 监听字段变化，处理通用级联逻辑（用于其他动态字段）
watch(() => props.modelValue, (newVal, oldVal) => {
  const allFields = props.fields || []
  
  // 检查每个字段的依赖（排除省市区，它们已经有专门的监听器）
  allFields.forEach(field => {
    if (field.dependsOn && field.apiUrl && !['city', 'district'].includes(field.prop)) {
      const dependValue = newVal?.[field.dependsOn]
      const oldDependValue = oldVal?.[field.dependsOn]
      
      // 如果依赖字段的值发生变化
      if (dependValue !== oldDependValue) {
        // 重新加载当前字段的选项
        loadFieldOptions(field)
        
        // 清空当前字段的值
        if (newVal[field.prop]) {
          const updated = { ...newVal }
          updated[field.prop] = null
          emit('update:modelValue', updated)
        }
        
        // 清空依赖当前字段的下级字段
        allFields.forEach(childField => {
          if (childField.dependsOn === field.prop && newVal[childField.prop]) {
            const updated = { ...newVal }
            updated[childField.prop] = null
            emit('update:modelValue', updated)
            // 清空下级字段的选项
            dynamicOptions[childField.prop] = []
          }
        })
      }
    }
  })
}, { deep: true })

// 监听 fields 变化，重新初始化
watch(() => props.fields, () => {
  initDynamicFields()
}, { immediate: true })

defineExpose({ scrollToSection })

// 保持双向绑定
const valueProxy = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', { ...(props.modelValue || {}), ...(val || {}) }),
})

</script>

<style scoped>
.config-switch-cell {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 32px;
}
.config-switch-label {
  font-size: 14px;
  color: #606266;
}
.meta-form-renderer {
  display: grid;
  grid-template-columns: 1fr;
  grid-auto-flow: row dense;
  gap: 8px 16px;
  width: 100%;
  max-width: none;
  box-sizing: border-box;
}
.meta-form-renderer .form-cell {
  box-sizing: border-box;
  min-width: 0;
  margin-right: 0;
  width: 100%;
}
.meta-form-renderer {
  --meta-form-control-width: 600px;
}
.meta-form-renderer :deep(.el-form-item) {
  width: 100%;
  max-width: none;
  margin: 0;
  align-items: flex-start;
}

.meta-form-renderer :deep(.el-form-item__label) {
  padding: 6px 16px 0 0;
  box-sizing: border-box;
  white-space: normal;
  word-break: break-word;
  line-height: 1.45;
  overflow: visible;
  height: auto;
  flex-shrink: 0;
}
.field-label {
  display: inline;
  white-space: normal;
  word-break: break-word;
  line-height: 1.45;
}
.meta-form-renderer :deep(.el-form-item__content) {
  flex: 0 0 auto;
  width: var(--meta-form-control-width) !important;
  max-width: var(--meta-form-control-width) !important;
  min-width: 0;
  justify-content: flex-start;
  padding-left: 0;
}
.meta-form-renderer :deep(.el-input),
.meta-form-renderer :deep(.el-input-number),
.meta-form-renderer :deep(.el-select),
.meta-form-renderer :deep(.el-tree-select),
.meta-form-renderer :deep(.el-date-editor) {
  width: var(--meta-form-control-width) !important;
  max-width: var(--meta-form-control-width) !important;
}
.meta-form-renderer :deep(.el-select .el-input),
.meta-form-renderer :deep(.el-select .el-input__wrapper) {
  width: 100% !important;
  max-width: 100% !important;
}
.category-tree-select {
  width: var(--meta-form-control-width) !important;
  max-width: var(--meta-form-control-width) !important;
}
.category-tree-select :deep(.el-select__wrapper) {
  width: 100% !important;
  min-height: 32px;
}
.meta-form-renderer :deep(.rich-text-editor) {
  width: 100%;
  max-width: 100%;
}
.meta-form-renderer :deep(.el-textarea) {
  width: var(--meta-form-control-width);
  max-width: var(--meta-form-control-width);
}
.meta-form-renderer :deep(.el-textarea__inner) {
  width: 100%;
  max-width: 100%;
}
.upload-field {
  width: 100%;
}
.upload-hidden {
  margin-top: 0;
}
.upload-placeholder {
  display: none;
}
.upload-field :deep(.el-upload-dragger) {
  padding: 0;
  border: none;
  background: transparent;
}
.upload-field :deep(.el-upload--picture-card) {
  width: 100%;
  height: auto;
}
.image-type .upload-field :deep(.el-upload--picture-card) {
  width: 120px;
  height: 120px;
  margin: 0;
  border-style: dashed;
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
.image-type .upload-field :deep(.el-upload-list__item) {
  width: 120px !important;
  height: 120px !important;
}
.image-type .upload-field :deep(.el-upload--picture-card .el-upload__text) {
  line-height: 1.2;
  margin-top: 2px;
}
.image-type .upload-plus {
  font-size: 20px;
  margin-bottom: 4px;
}
.upload-field:hover .upload-placeholder {
  border-color: #409eff;
  color: #409eff;
  background: #f5f9ff;
}
.form-groups {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  box-sizing: border-box;
}
.group-card {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 16px;
  background: #fff;
  box-shadow: 0 2px 10px rgba(17, 24, 39, 0.06);
  width: 100%;
  max-width: none;
  margin: 0;
  box-sizing: border-box;
}
.group-card.muted {
  background: #f9fafb;
}
.group-title {
  font-weight: 600;
  margin-bottom: 12px;
}
.sub-table-container {
  width: 100%;
  overflow-x: auto;
}
.sub-table-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 160px;
  color: #909399;
  font-size: 14px;
  background: #fafafa;
  border: 1px solid #ebeef5;
  border-radius: 4px;
}
.sub-table-state__icon {
  font-size: 18px;
}
.sub-table-inline-input {
  width: 100%;
}
.sub-table-number-input {
  width: 100%;
}
.sub-table-readonly {
  color: #909399;
  font-size: 12px;
}
.sub-table-upload-block {
  padding: 4px 0;
}
.sub-table-upload-block .image-thumb-list {
  margin-top: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  grid-template-columns: unset;
  max-width: 380px;
}
.sub-table-container :deep(.el-table .cell) {
  overflow: visible;
}
.sub-table-container :deep(.el-table__body-wrapper) {
  height: auto !important;
  max-height: none !important;
  overflow-y: visible !important;
}
.sub-table-status-cell {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.sub-table-status-label {
  font-size: 12px;
  color: #606266;
}
.sub-table-actions {
  margin-top: 12px;
  display: flex;
  justify-content: flex-start;
}
.category-tabs {
  display: block;
  margin-bottom: 8px;
  width: 100%;
}
.category-tabs :deep(.el-tabs__header) {
  margin: 0 auto;
}
.category-tabs :deep(.el-tabs__nav) {
  display: inline-flex;
}
.category-tabs :deep(.el-tabs__nav-wrap) {
  display: flex;
  justify-content: center;
}
.cat-tab {
  padding: 6px 10px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  cursor: pointer;
  color: #606266;
  background: #f5f7fa;
  user-select: none;
}
.cat-tab.active {
  color: #409eff;
  border-color: #409eff;
  background: #ecf5ff;
}
.meta-form-renderer {
  width: 100%;
  max-width: none;
  margin: 0;
}
.upload-block {
  width: 100%;
}
.upload-head {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 8px;
  margin-bottom: 6px;
}
.thumb-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 10px;
  margin-top: 8px;
}
.thumb-card {
  border: 1px solid #e5e6eb;
  border-radius: 6px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  align-items: stretch;
}
.thumb-img {
  width: 100%;
  padding-top: 100%;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}
.thumb-remove {
  border: none;
  border-top: 1px solid #f5f5f5;
  border-radius: 0;
}
.image-thumb-list {
  margin-top: 12px;
}
.image-thumb-list .thumb-card {
  width: 120px;
  height: 120px;
}
.image-thumb-list .thumb-img {
  width: 100%;
  height: 100%;
  padding-top: 0;
}
.image-thumb-list--sortable .thumb-card--draggable {
  cursor: grab;
}
.image-thumb-list--sortable .thumb-card--draggable:active {
  cursor: grabbing;
}
.thumb-card--ghost {
  opacity: 0.45;
}
.thumb-card--chosen {
  box-shadow: 0 4px 14px rgba(64, 158, 255, 0.35);
}
.thumb-card--add {
  width: 120px;
  height: 120px;
  border-style: dashed;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #909399;
  background: #fafafa;
}
.thumb-card--add:hover {
  border-color: #409eff;
  color: #409eff;
}
</style>
